import { useEffect, useState } from 'react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Add, Block, Close, ExpandMore, MoreVert } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { fontSize } from '../..';
import {
  getDatasetSchedule,
  deleteDatasetSchedule,
  updateDatasetScheduleStatus,
} from '../../api/dataset';
import CreateSchedule from './CreateSchedule';
import PastNextRun from './PastNextRun';
import { AcceptRejectDialog, NotificationDialog } from '../CommonFunctions/DialogComponents';
import { IDatasetSchedule } from '../../api/types/dataset';

interface ScheduleRefreshProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDataset: { workspaceId: string; datasetId: string };
}

export default function ScheduleRefresh({ open, setOpen, selectedDataset }: ScheduleRefreshProps) {
  const [schedules, setSchedules] = useState<IDatasetSchedule[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createSchedule, setCreateSchedule] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<IDatasetSchedule | null>(null);
  const [loading, setLoading] = useState(false);

  const [showDetails, setShowDetails] = useState(false);
  const [viewSchedule, setViewSchedule] = useState<boolean>(false);

  const [alert, setAlert] = useState<{
    severity: 'success' | 'info' | 'warning' | 'error';
    message: string;
    show: boolean;
  } | null>(null);

  const [statusDialog, setStatusDialog] = useState<{
    open: boolean;
    type: 'update' | 'delete' | null;
  }>({ open: false, type: null });
  const [heading, setHeading] = useState('');
  const [messages, setMessages] = useState('');

  const fetchDatasetSchedule = async () => {
    if (!selectedDataset || !selectedDataset?.workspaceId || !selectedDataset?.datasetId) return;
    setLoading(true);
    const { data } = await getDatasetSchedule(
      selectedDataset.datasetId,
      selectedDataset.workspaceId,
    );
    // console.log(data);
    setSchedules(data);
    setLoading(false);
  };

  const handleChangeStatus = async () => {
    if (!selectedSchedule) return;
    if (!selectedDataset || !selectedDataset?.workspaceId || !selectedDataset?.datasetId) return;
    const { isError } = await updateDatasetScheduleStatus(
      selectedDataset.datasetId,
      selectedDataset.workspaceId,
      selectedSchedule.id,
      !selectedSchedule.enabled,
    );
    setSchedules((prevSchedules) =>
      prevSchedules.map((schedule) =>
        schedule.id === selectedSchedule.id
          ? { ...schedule, enabled: !selectedSchedule.enabled }
          : schedule,
      ),
    );
    setAlert({
      severity: isError ? 'error' : 'success',
      message: isError
        ? 'Could not update the schedule status. Please try again.'
        : `Schedule has been ${selectedSchedule.enabled ? 'disabled' : 'enabled'} successfully.`,
      show: true,
    });
    setTimeout(() => {
      setAlert(null);
    }, 2000);
    setShowDetails(false);
    setSelectedSchedule(null);
    handleMenuClose();
  };

  const handleDeleteSchedule = async () => {
    if (!selectedDataset || !selectedDataset?.workspaceId || !selectedDataset?.datasetId) return;
    if (!selectedSchedule) return;
    const { isError, message } = await deleteDatasetSchedule(
      selectedDataset.datasetId,
      selectedDataset.workspaceId,
      selectedSchedule.id,
    );
    setSchedules((prevSchedules) =>
      prevSchedules.filter((schedule) => schedule.id !== selectedSchedule.id),
    );
    setAlert({
      severity: isError ? 'error' : 'success',
      message: isError
        ? `Could not delete the schedule. Please try again.`
        : `Schedule "${selectedSchedule.name}" has been deleted successfully.`,
      show: true,
    });
    setTimeout(() => {
      setAlert(null);
    }, 2000);
    setShowDetails(false);
    setSelectedSchedule(null);
    handleMenuClose();
  };

  const handleViewDetails = () => {
    if (!selectedSchedule) return;
    setViewSchedule(true);
    setShowDetails(false);
    setCreateSchedule(true);
    handleMenuClose();
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, schedule: IDatasetSchedule) => {
    setAnchorEl(event.currentTarget);
    setSelectedSchedule(schedule);
    setShowDetails(false);
  };

  const handleCreateSchedule = () => {
    setCreateSchedule(true);
    setSelectedSchedule(null);
    handleMenuClose();
  };

  const selectedASchedule = (schedule: IDatasetSchedule) => {
    if (!schedule) return;
    if (!schedule?.enabled) return;
    if (selectedSchedule?.id === schedule?.id) {
      setSelectedSchedule(null);
      setShowDetails(false);
      return;
    }
    setSelectedSchedule(schedule);
    setShowDetails(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // setSelectedSchedule(null);
    setShowDetails(false);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedSchedule(null);
  };

  useEffect(() => {
    fetchDatasetSchedule();
  }, [selectedDataset]);

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: 'row',
            gap: 2,
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Dataset Refresh Schedule
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip
              title={
                schedules.length >= 3
                  ? 'You can only create up to 3 schedules per dataset.'
                  : 'Create a new schedule'
              }
              arrow
              placement="top"
            >
              <span>
                <Button
                  onClick={handleCreateSchedule}
                  disabled={schedules.length >= 3}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#2bb9bb',
                    color: '#fff',
                    border: 'none',
                    '&:hover': {
                      backgroundColor: '#14a1a3ff',
                      color: '#fff',
                      border: 'none',
                    },
                    ':disabled': {
                      color: '#ffffffca',
                    },
                    opacity: schedules.length >= 3 ? 0.75 : 1,
                    cursor: schedules.length >= 3 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Add fontSize="small" sx={{ mb: '0.1rem', gap: '0.15rem' }} />
                  New
                </Button>
              </span>
            </Tooltip>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: 300,
              overflow: 'hidden',
            }}
            onClick={() => {
              setSelectedSchedule(null);
              setShowDetails(false);
            }}
          >
            <TableContainer
              component={Paper}
              sx={{
                '&.MuiPaper-root': {
                  width: '100%',
                  overflow: 'auto',
                  border: 'none',
                },
              }}
            >
              {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress size={18} sx={{ color: '#2bb9bb' }} />
                </Box>
              )}
              {!loading && schedules.length === 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 300,
                  }}
                >
                  <Typography variant="body1" color="textSecondary">
                    No schedules found. Click "New" to create a schedule.
                  </Typography>
                </Box>
              )}
              {!loading && schedules.length > 0 && (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ width: 10, minWidth: 10, maxWidth: 10, padding: '0 4px' }}
                      ></TableCell>
                      <TableCell sx={{ width: 140, minWidth: 140, maxWidth: 140 }}>
                        <strong>Name</strong>
                      </TableCell>
                      <TableCell sx={{ width: 140, minWidth: 140, maxWidth: 140 }}>
                        <strong>Schedule Type</strong>
                      </TableCell>
                      <TableCell sx={{ width: 140, minWidth: 140, maxWidth: 140 }}>
                        <strong>Date Created</strong>
                      </TableCell>
                      <TableCell
                        sx={{ width: 30, minWidth: 30, maxWidth: 30, padding: '0 4px' }}
                      ></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody
                    sx={{
                      fontSize: fontSize.medium,
                    }}
                  >
                    {schedules.map((schedule: IDatasetSchedule, index: number) => (
                      <Tooltip
                        key={`${schedule?.id}-${index}`}
                        title="Disabled"
                        placement="top"
                        disableHoverListener={schedule?.enabled}
                      >
                        <TableRow
                          onClick={(e) => {
                            e.stopPropagation();
                            selectedASchedule(schedule);
                          }}
                          sx={{
                            padding: 0,
                            bgcolor: !schedule?.enabled
                              ? '#f5f5f5'
                              : selectedSchedule?.id === schedule?.id && !anchorEl
                                ? '#fafafaff'
                                : '',
                            cursor: schedule?.enabled ? 'pointer' : 'not-allowed',
                            '& .MuiTableCell-root': {
                              paddingTop: '4px',
                              paddingBottom: '4px',
                            },
                            '&:last-child td, &:last-child th': {
                              border: 0,
                            },
                          }}
                        >
                          <TableCell
                            sx={{ width: 10, minWidth: 10, maxWidth: 10, padding: '0 4px' }}
                          >
                            {!schedule?.enabled && (
                              <Block
                                fontSize="small"
                                sx={{ color: '#404040', mt: 0.5, p: '0.1rem' }}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              width: 140,
                              minWidth: 140,
                              maxWidth: 140,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={schedule?.name}
                          >
                            {schedule?.name}
                          </TableCell>
                          <TableCell sx={{ width: 140, minWidth: 140, maxWidth: 140 }}>
                            {schedule?.type}
                          </TableCell>
                          <TableCell sx={{ width: 140, minWidth: 140, maxWidth: 140 }}>
                            {schedule?.createdAt
                              ? formatDistanceToNow(parseISO(schedule.createdAt), {
                                  addSuffix: true,
                                })
                              : 'Just now'}
                          </TableCell>
                          <TableCell
                            sx={{ width: 30, minWidth: 30, maxWidth: 30, padding: '0 4px' }}
                          >
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuClick(e, schedule);
                              }}
                              size="small"
                              sx={{
                                padding: '4px',
                                cursor: 'pointer',
                              }}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </Tooltip>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>

            {schedules.length > 0 && showDetails && (
              <PastNextRun selectedSchedule={selectedSchedule} selectedDataset={selectedDataset} />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Menu */}
      <Menu
        open={Boolean(anchorEl)}
        onClose={() => {
          handleMenuClose();
          setTimeout(() => {
            // To avoid the menu item status getting updated before the menu is closed
            setSelectedSchedule(null);
          }, 200);
        }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        <MenuItem
          disabled={!selectedSchedule?.enabled}
          onClick={() => {
            console.log(selectedSchedule);
            if (!selectedSchedule) return;
            setSelectedSchedule(selectedSchedule);
            setShowDetails(true);
            setAnchorEl(null);
          }}
        >
          Next/Past Runs
        </MenuItem>
        <MenuItem
          onClick={() => {
            setStatusDialog({ open: true, type: 'update' });
            setHeading('Change Schedule Status');
            setMessages(
              `Are you sure you want to ${selectedSchedule?.enabled ? 'disable' : 'enable'} this schedule?`,
            );
          }}
        >
          {selectedSchedule?.enabled ? 'Disable' : 'Enable'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setStatusDialog({ open: true, type: 'delete' });
            setHeading('Delete Schedule');
            setMessages('Are you sure you want to delete this schedule?');
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      {createSchedule && (
        <CreateSchedule
          open={createSchedule}
          setOpen={setCreateSchedule}
          selectedDataset={selectedDataset}
          fetchDatasetSchedule={fetchDatasetSchedule}
          selectedSchedule={viewSchedule ? selectedSchedule : null}
          setSelectedSchedule={setSelectedSchedule}
          setViewSchedule={setViewSchedule}
          viewSchedule={viewSchedule}
          schedules={schedules}
        />
      )}

      <AcceptRejectDialog
        open={statusDialog.open}
        heading={heading}
        messages={[
          {
            text: messages,
          },
        ]}
        acceptFunction={() => {
          console.log(statusDialog.type);
          if (statusDialog.type === 'update') {
            handleChangeStatus();
          }
          if (statusDialog.type === 'delete') {
            handleDeleteSchedule();
          }
          handleMenuClose();
          setStatusDialog({ open: false, type: null });
        }}
        rejectFunction={() => {
          handleMenuClose();
          setStatusDialog({ open: false, type: null });
        }}
        acceptText="Yes"
        rejectText="No"
      />

      {alert && alert.show && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert.show}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
    </>
  );
}
